import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const BRAND = "DigiBooks";

// Shown once on app boot — an architectural blueprint of a house draws itself
// line by line, then lights up and gives way to the brand mark. Fades out on
// its own after durationMs.
const SplashScreen = ({ onFinished, durationMs = 4800 }) => {
  const [fadingOut, setFadingOut] = useState(false);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadingOut(true), durationMs);
    return () => clearTimeout(fadeTimer);
  }, [durationMs]);

  useEffect(() => {
    if (!fadingOut) return;
    const unmountTimer = setTimeout(() => {
      setMounted(false);
      if (onFinished) onFinished();
    }, 650);
    return () => clearTimeout(unmountTimer);
  }, [fadingOut, onFinished]);

  if (!mounted) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: "linear-gradient(160deg, #081b3a 0%, #0d2b5c 55%, #123a75 100%)",
        opacity: fadingOut ? 0 : 1,
        visibility: fadingOut ? "hidden" : "visible",
        transition: "opacity 0.6s ease, visibility 0.6s ease",
      }}
    >
      {/* Blueprint grid texture, slowly drifting */}
      <Box
        sx={{
          position: "absolute",
          inset: "-42px",
          backgroundImage:
            "linear-gradient(rgba(120,170,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(120,170,255,0.09) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
          animation: "arcdxBlueprintGrid 9s linear infinite",
        }}
      />

      {/* The house — drawn stroke by stroke */}
      <Box sx={{ position: "relative", width: 220, height: 220 }}>
        <svg viewBox="0 0 220 220" width="220" height="220" style={{ overflow: "visible" }}>
          {/* soft interior glow behind the window, appears once the house is lit */}
          <circle
            cx="74"
            cy="121"
            r="26"
            fill="url(#arcdxGlowGradient)"
            style={{
              opacity: 0,
              animation: "arcdxBlueprintGlow 0.8s ease forwards 2.9s, arcdxBlueprintGlowPulse 2.4s ease-in-out infinite 3.7s",
            }}
          />
          <defs>
            <radialGradient id="arcdxGlowGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffd580" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ffd580" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Faint fill that settles in behind the outline */}
          <path
            d="M50,170 L50,95 L110,45 L170,95 L170,170 Z"
            fill="#4db2fc"
            style={{ opacity: 0, animation: "arcdxBlueprintFill 0.8s ease forwards 2.0s" }}
          />

          {/* House outline (roof + walls) */}
          <path
            d="M50,170 L50,95 L110,45 L170,95 L170,170 L50,170 Z"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              animation: "arcdxBlueprintDraw 1.7s ease forwards 0.2s",
            }}
          />

          {/* Door */}
          <path
            d="M96,170 L96,124 L132,124 L132,170"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              animation: "arcdxBlueprintDraw 0.7s ease forwards 2.0s",
            }}
          />

          {/* Window */}
          <rect
            x="61"
            y="108"
            width="26"
            height="26"
            rx="2"
            fill="none"
            stroke="#eaf4ff"
            strokeWidth="3"
            pathLength="1"
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              animation: "arcdxBlueprintDraw 0.6s ease forwards 2.4s",
            }}
          />
          <path
            d="M74,108 L74,134 M61,121 L87,121"
            stroke="#eaf4ff"
            strokeWidth="2"
            pathLength="1"
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              animation: "arcdxBlueprintDraw 0.4s ease forwards 3.0s",
            }}
          />
        </svg>
      </Box>

      {/* Brand mark */}
      <Box
        sx={{
          mt: 3,
          opacity: 0,
          animation: "arcdxBlueprintWordUp 0.7s ease forwards 3.3s",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "32px", md: "40px" },
            fontWeight: 800,
            color: "#fff",
            fontFamily: "Roboto, Arial, sans-serif",
            letterSpacing: "0.14em",
          }}
        >
          {BRAND}
        </Typography>
        <Box
          sx={{
            width: "72px",
            height: "3px",
            borderRadius: "999px",
            background: "linear-gradient(90deg, #4db2fc, #ffd580)",
            margin: "6px auto 0",
            transformOrigin: "center",
            animation: "arcdxBlueprintUnderline 0.6s ease forwards 3.7s",
            transform: "scaleX(0)",
          }}
        />
        <Typography
          sx={{
            fontSize: "12.5px",
            fontWeight: 600,
            color: "rgba(234,244,255,0.75)",
            fontFamily: "Roboto, Arial, sans-serif",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            mt: 1.25,
            opacity: 0,
            animation: "arcdxBlueprintTagline 0.6s ease forwards 4.0s",
          }}
        >
          Crafting Beautiful Spaces
        </Typography>
      </Box>

      {/* Minimal loading dots */}
      <Box sx={{ display: "flex", gap: "7px", mt: 3.5 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#4db2fc",
              opacity: 0,
              animation: "arcdxBlueprintDot 1.2s ease-in-out infinite",
              animationDelay: `${4.3 + i * 0.15}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SplashScreen;
